import React, { useState, useCallback } from 'react';
import useXHR from '../../hooks/useXHR';
import { API } from '../../utils/api';
/**
 * ShareProtocolButton - Button that generates and displays a shareable link for a protocol
 */
export const ShareProtocolButton = ({ categoryId, categoryName, protocol, token, showToast }) => {
  const { get, post, del: xhrDel, put } = useXHR(token);
  const [showModal, setShowModal] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const generateShareLink = useCallback(async () => {
    if (!token) {
      showToast && showToast('You must be logged in to share', 'error');
      return;
    }
    
    setLoading(true);
    try {
      const data = await post(`/categories/${categoryId}/share-link`);
      setShareData(data);
      setShowModal(true);
    } catch (e) {
      console.error('Share error:', e);
      showToast && showToast(typeof e === 'string' ? e : 'Failed to generate share link', 'error');
    }
    setLoading(false);
  }, [categoryId, post, showToast]);
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showToast && showToast('Link copied to clipboard!', 'success');
  };
  
  const shareUrl = shareData ? `${window.location.origin}${shareData.share_url}` : '';
  
  return (
    <>
      <button
        onClick={generateShareLink}
        disabled={loading}
        data-testid="share-protocol-btn"
        title="Share this protocol"
        style={{
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(6, 182, 212, 0.2))',
          border: '1px solid rgba(16, 185, 129, 0.4)',
          borderRadius: 6,
          padding: '4px 10px',
          color: '#10b981',
          fontSize: '0.75rem',
          cursor: loading ? 'wait' : 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          transition: 'all 0.2s'
        }}
      >
        {loading ? '...' : '🔗'} Share
      </button>
      
      {/* Share Modal */}
      {showModal && shareData && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'linear-gradient(135deg, #FFFFFF, #2d1b4e)',
              borderRadius: 20,
              padding: 30,
              maxWidth: 500,
              width: '90%',
              border: '2px solid rgba(252, 211, 77, 0.4)',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
            }}
          >
            {/* Header */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: 20
            }}>
              <h3 style={{ color: '#0F172A', margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: '1.5rem' }}>🔗</span> Share Protocol
              </h3>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: 'none',
                  borderRadius: '50%',
                  width: 32,
                  height: 32,
                  color: '#0F172A',
                  cursor: 'pointer',
                  fontSize: '1.2rem'
                }}
              >
                ×
              </button>
            </div>
            
            {/* Protocol Info */}
            <div style={{
              background: 'rgba(252, 211, 77, 0.2)',
              borderRadius: 12,
              padding: 15,
              marginBottom: 20
            }}>
              <div style={{ color: '#047857', fontSize: '0.75rem', marginBottom: 4 }}>
                Category Name
              </div>
              <div style={{ color: '#0F172A', fontWeight: 600, marginBottom: 12 }}>
                {categoryName}
              </div>
              <div style={{ color: '#047857', fontSize: '0.75rem', marginBottom: 4 }}>
                Protocol
              </div>
              <code style={{
                background: 'rgba(0, 0, 0, 0.3)',
                padding: '8px 12px',
                borderRadius: 6,
                color: '#10b981',
                fontSize: '0.85rem',
                display: 'block',
                wordBreak: 'break-all'
              }}>
                {protocol}
              </code>
            </div>
            
            {/* Share Link */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ color: '#047857', fontSize: '0.75rem', marginBottom: 8 }}>
                Shareable Link
              </div>
              <div style={{
                display: 'flex',
                gap: 8
              }}>
                <input
                  type="text"
                  value={shareUrl}
                  readOnly
                  style={{
                    flex: 1,
                    background: 'rgba(0, 0, 0, 0.3)',
                    border: '1px solid rgba(252, 211, 77, 0.3)',
                    borderRadius: 8,
                    padding: '10px 14px',
                    color: '#0F172A',
                    fontSize: '0.85rem'
                  }}
                />
                <button
                  onClick={() => copyToClipboard(shareUrl)}
                  style={{
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    border: 'none',
                    borderRadius: 8,
                    padding: '10px 20px',
                    color: '#0F172A',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Copy
                </button>
              </div>
            </div>
            
            {/* Share Stats */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 12,
              marginBottom: 20
            }}>
              <div style={{
                background: 'rgba(245, 158, 11, 0.2)',
                borderRadius: 10,
                padding: 12,
                textAlign: 'center'
              }}>
                <div style={{ color: '#3b82f6', fontSize: '1.5rem', fontWeight: 700 }}>
                  {shareData.view_count}
                </div>
                <div style={{ color: '#93c5fd', fontSize: '0.75rem' }}>
                  Views
                </div>
              </div>
              <div style={{
                background: 'rgba(16, 185, 129, 0.2)',
                borderRadius: 10,
                padding: 12,
                textAlign: 'center'
              }}>
                <div style={{ color: '#10b981', fontSize: '1.5rem', fontWeight: 700 }}>
                  {shareData.copy_count}
                </div>
                <div style={{ color: '#6ee7b7', fontSize: '0.75rem' }}>
                  Copies
                </div>
              </div>
            </div>
            
            {/* Social Share Buttons */}
            <div style={{ 
              display: 'flex', 
              gap: 10, 
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <a
                href={`https://twitter.com/intent/tweet?text=Check out this search protocol: "${categoryName}"&url=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  background: '#1da1f2',
                  color: '#0F172A',
                  padding: '8px 16px',
                  borderRadius: 20,
                  fontSize: '0.8rem',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6
                }}
              >
                🐦 Twitter
              </a>
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  background: '#0077b5',
                  color: '#0F172A',
                  padding: '8px 16px',
                  borderRadius: 20,
                  fontSize: '0.8rem',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6
                }}
              >
                💼 LinkedIn
              </a>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(
                    `Check out this search protocol on InfoPilot:\n"${categoryName}"\nProtocol: ${protocol}\n${shareUrl}`
                  );
                  showToast && showToast('Copied full message!', 'success');
                }}
                style={{
                  background: 'linear-gradient(135deg, #FDE047, #FCD34D)',
                  color: '#0F172A',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: 20,
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6
                }}
              >
                📋 Copy Message
              </button>
            </div>
            
            {/* Book promotion in share modal */}
            <div style={{
              marginTop: 20,
              padding: 12,
              background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.1), rgba(245, 158, 11, 0.1))',
              borderRadius: 10,
              textAlign: 'center',
              border: '1px solid rgba(251, 191, 36, 0.3)'
            }}>
              <span style={{ fontSize: '0.75rem', color: '#047857' }}>
                📚 Powered by InfoPilot Explorer - Also check out "Letters to Evelyn" by John Selman!
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/**
 * SharedProtocolViewer - Page component for viewing shared protocols
 */
export const SharedProtocolViewer = ({ shareCode, token, showToast, onCopy }) => {
  const [sharedData, setSharedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copying, setCopying] = useState(false);
  const [error, setError] = useState(null);
  
  React.useEffect(() => {
    const fetchSharedProtocol = async () => {
      try {
        const data = await get(`/share/${shareCode}`);
        setSharedData(data);
      } catch (e) {
        setError('This share link is invalid or has expired');
      }
      setLoading(false);
    };
    
    fetchSharedProtocol();
  }, [shareCode]);
  
  const handleCopy = async () => {
    if (!token) {
      showToast && showToast('Please log in to copy this protocol', 'warning');
      return;
    }
    
    setCopying(true);
    try {
      const data = await post(`/share/${shareCode}/copy`);
      if (data.already_exists) {
        showToast && showToast('You already have this protocol!', 'info');
      } else {
        showToast && showToast('Protocol copied to your categories!', 'success');
        onCopy && onCopy(data);
      }
    } catch (e) {
      showToast && showToast(typeof e === 'string' ? e : 'Failed to copy protocol', 'error');
    }
    setCopying(false);
  };
  
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        minHeight: 300 
      }}>
        <div style={{ color: '#047857' }}>Loading shared protocol...</div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div style={{
        background: 'rgba(239, 68, 68, 0.1)',
        border: '1px solid rgba(239, 68, 68, 0.3)',
        borderRadius: 12,
        padding: 30,
        textAlign: 'center',
        color: '#f87171'
      }}>
        <div style={{ fontSize: '3rem', marginBottom: 15 }}>😢</div>
        <div>{error}</div>
      </div>
    );
  }
  
  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.95), rgba(15, 23, 42, 0.95))',
      borderRadius: 20,
      padding: 30,
      border: '2px solid rgba(252, 211, 77, 0.3)',
      maxWidth: 600,
      margin: '0 auto'
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 25 }}>
        <div style={{ fontSize: '3rem', marginBottom: 10 }}>🔗</div>
        <h2 style={{ color: '#0F172A', margin: 0 }}>Shared Protocol</h2>
        <div style={{ color: '#047857', fontSize: '0.85rem', marginTop: 5 }}>
          Shared by <strong>@{sharedData.owner_username}</strong>
        </div>
      </div>
      
      {/* Protocol Card */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(252, 211, 77, 0.2), rgba(253, 224, 71, 0.1))',
        borderRadius: 15,
        padding: 20,
        marginBottom: 20
      }}>
        <div style={{ color: '#c4b5fd', fontSize: '0.75rem', marginBottom: 5 }}>
          Category Name
        </div>
        <h3 style={{ color: '#0F172A', margin: '0 0 15px 0' }}>
          {sharedData.category_name}
        </h3>
        
        <div style={{ color: '#c4b5fd', fontSize: '0.75rem', marginBottom: 5 }}>
          Protocol
        </div>
        <code style={{
          display: 'block',
          background: 'rgba(0, 0, 0, 0.4)',
          padding: '12px 16px',
          borderRadius: 8,
          color: '#10b981',
          fontSize: '0.95rem',
          wordBreak: 'break-all',
          border: '1px solid rgba(16, 185, 129, 0.3)'
        }}>
          {sharedData.protocol}
        </code>
      </div>
      
      {/* Stats */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: 30,
        marginBottom: 20
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#3b82f6', fontSize: '1.3rem', fontWeight: 700 }}>
            {sharedData.view_count}
          </div>
          <div style={{ color: '#93c5fd', fontSize: '0.7rem' }}>Views</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#10b981', fontSize: '1.3rem', fontWeight: 700 }}>
            {sharedData.copy_count}
          </div>
          <div style={{ color: '#6ee7b7', fontSize: '0.7rem' }}>Copies</div>
        </div>
      </div>
      
      {/* Copy Button */}
      <button
        onClick={handleCopy}
        disabled={copying}
        style={{
          width: '100%',
          background: copying 
            ? 'rgba(107, 114, 128, 0.5)' 
            : 'linear-gradient(135deg, #10b981, #059669)',
          border: 'none',
          borderRadius: 12,
          padding: '14px 24px',
          color: '#0F172A',
          fontWeight: 700,
          fontSize: '1rem',
          cursor: copying ? 'wait' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8
        }}
      >
        {copying ? 'Copying...' : '📋 Copy to My Categories'}
      </button>
      
      {!token && (
        <div style={{
          marginTop: 12,
          textAlign: 'center',
          color: '#047857',
          fontSize: '0.8rem'
        }}>
          Log in to copy this protocol to your account
        </div>
      )}
    </div>
  );
};

export default ShareProtocolButton;
